import { useMemo } from 'react';
import { appNavGroups, type AppNavGroup } from '@/shared/config/appNav';
import {
  isAgencyRole,
  isClientRole,
  isTeamMemberRole,
} from '@/shared/config/roles';
import { SUB_ACCOUNT_ROLE } from '@/shared/config/permissions';
import type { AuthUser } from '@/shared/types/auth';

type PermissionEntry = string | { name?: string; permission?: string };

function hasPermission(
  permissions: PermissionEntry[],
  isSubAccount: boolean,
  permission?: string,
) {
  if (!permission || !isSubAccount) return true;
  return permissions.some(
    (entry) =>
      entry === permission ||
      (typeof entry === 'object' &&
        (entry.name === permission || entry.permission === permission)),
  );
}

export function useAppNavGroups(user: AuthUser | null | undefined): AppNavGroup[] {
  const role = user?.role || '';
  const isAgency = isAgencyRole(role);
  const isClient = isClientRole(role);
  const isTeamMember = isTeamMemberRole(role);
  const isSubAccount = role === SUB_ACCOUNT_ROLE;

  return useMemo(() => {
    const permissions = (user?.permissions || []) as PermissionEntry[];
    return appNavGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (item.agencyOnly && !isAgency) return false;
          if (isClient && item.hideForClient) return false;
          if (isTeamMember && item.hideForTeamMember) return false;
          // Team members always see Campaign (API scopes to assigned clients).
          if (
            isTeamMember &&
            item.to === '/campaigns'
          ) {
            return true;
          }
          if (
            item.permission &&
            !hasPermission(permissions, isSubAccount, item.permission)
          ) {
            return false;
          }
          return true;
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [isAgency, isClient, isTeamMember, isSubAccount, user?.permissions]);
}

export function formatRoleLabel(role?: string) {
  if (!role) return 'Advertiser';
  return role
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
